// ===========================================
// Student Bookings Section (Admin)
// ===========================================
// Section for student edit modal showing makeup credits, eligibilities,
// and extra lesson requests

import { useState } from 'react';
import { format } from 'date-fns';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  Autorenew as MakeupIcon,
  EventAvailable as SlotIcon,
  AddCircleOutline as ExtraIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckIcon,
  HourglassEmpty as PendingIcon,
  Close as RejectIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import {
  useAdminStudentCredits,
  useAdminGrantCredit,
  useAdminMakeupEligibilities,
  useAdminExtraLessonRequests,
  useAdminApproveExtraLesson,
  useAdminRejectExtraLesson,
} from '../../hooks/useBooking';
import { useTerms } from '../../hooks/useAdmin';
import { ExtraLessonRequestStatus, MakeupEligibility } from '../../api/booking.api';
import { formatUTCDate, localDateFromKey } from '../../utils/dateUtils';
import { useSchoolTimezone } from '../../contexts/TenantContext';

// ===========================================
// TYPES
// ===========================================

interface StudentBookingsSectionProps {
  studentId: string;
  studentName: string;
}

/**
 * The two ways to add make-up credit (#574). SEND gives the family a Book button on their make-up
 * page; ADD MORE raises the balance only, for the office to use from Book Extra Class.
 */
type CreditMode = 'send' | 'add';

interface GrantCreditModalState {
  open: boolean;
  mode: CreditMode;
  termId: string;
  additionalCredits: number;
  reason: string;
}

interface RejectModalState {
  open: boolean;
  requestId: string;
  adminNotes: string;
}

// ===========================================
// HELPERS
// ===========================================

/** A calendar day as dd/mm/yyyy (Critical Rule 7), read with UTC getters (`@db.Date` values). */
const dayAu = (value: string): string =>
  formatUTCDate(value, { day: '2-digit', month: '2-digit', year: 'numeric' });

/** The school-local day an INSTANT fell on, as dd/mm/yyyy (not for `@db.Date` values: see dayAu). */
const instantDayAu = (iso: string, timeZone: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone }).format(d);
};

/** `Tuesday 03/11/2026`: the weekday a lesson is remembered by, then the day as dd/mm/yyyy. */
const weekdayDayAu = (value: string): string =>
  `${formatUTCDate(value, { weekday: 'long' })} ${dayAu(value)}`;

/** Where, when and with whom a make-up offer was booked, or null if no slot is attached. */
function bookedSlotLine(elig: MakeupEligibility): string | null {
  const slot = elig.MakeupSlot;
  if (!slot) return null;
  const teacher = slot.Teacher?.User ? `${slot.Teacher.User.firstName} ${slot.Teacher.User.lastName}` : null;
  const room = slot.Room ? `${slot.Room.Location?.name ?? ''}${slot.Room.Location?.name ? ' - ' : ''}${slot.Room.name}` : 'Room TBA';
  return [
    `${weekdayDayAu(slot.date)}, ${slot.startTime} - ${slot.endTime}`,
    [slot.Instrument?.name, teacher && `with ${teacher}`].filter(Boolean).join(' '),
    room,
  ]
    .filter(Boolean)
    .join(' · ');
}

const getStatusColor = (status: ExtraLessonRequestStatus) => {
  switch (status) {
    case 'PENDING':
      return 'warning';
    case 'APPROVED':
      return 'info';
    case 'SCHEDULED':
      return 'primary';
    case 'COMPLETED':
      return 'success';
    case 'REJECTED':
      return 'error';
    case 'CANCELLED':
      return 'default';
    default:
      return 'default';
  }
};

const getStatusIcon = (status: ExtraLessonRequestStatus) => {
  switch (status) {
    case 'PENDING':
      return <PendingIcon fontSize="small" />;
    case 'APPROVED':
    case 'COMPLETED':
      return <CheckIcon fontSize="small" />;
    case 'SCHEDULED':
      return <ScheduleIcon fontSize="small" />;
    case 'REJECTED':
      return <RejectIcon fontSize="small" />;
    case 'CANCELLED':
      return <CancelIcon fontSize="small" />;
    default:
      return null;
  }
};

// ===========================================
// COMPONENT
// ===========================================

export default function StudentBookingsSection({
  studentId,
  studentName,
}: StudentBookingsSectionProps) {
  // State
  const [grantCreditModal, setGrantCreditModal] = useState<GrantCreditModalState>({
    open: false,
    mode: 'add',
    termId: '',
    additionalCredits: 1,
    reason: '',
  });
  const [rejectModal, setRejectModal] = useState<RejectModalState>({
    open: false,
    requestId: '',
    adminNotes: '',
  });
  const [expandedPanel, setExpandedPanel] = useState<string | false>('credits');

  // The day an INSTANT (`redeemedAt`) fell on is the SCHOOL's day, not the UTC one: a booking made at
  // 08:00 Sydney is still the previous day in UTC (`@db.Date` values are the opposite: UTC getters).
  const schoolTimezone = useSchoolTimezone();

  // Queries
  const { data: terms } = useTerms();
  const { data: eligibilities, isLoading: eligibilitiesLoading } = useAdminMakeupEligibilities({
    studentId,
  });
  const { data: extraRequests, isLoading: requestsLoading } = useAdminExtraLessonRequests({
    studentId,
  });

  // Get current term for credits query
  const currentTerm = terms?.find((t) => {
    const now = new Date();
    return new Date(t.startDate) <= now && new Date(t.endDate) >= now && t.isActive;
  });
  const { data: credits, isLoading: creditsLoading } = useAdminStudentCredits(
    studentId,
    currentTerm?.id || ''
  );

  // Mutations
  const grantCredit = useAdminGrantCredit();
  const approveRequest = useAdminApproveExtraLesson();
  const rejectRequest = useAdminRejectExtraLesson();

  // Handlers
  const handlePanelChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedPanel(isExpanded ? panel : false);
  };

  const handleOpenGrantCreditModal = (mode: CreditMode) => {
    grantCredit.reset(); // a previous failure must not greet the next open
    setGrantCreditModal({
      open: true,
      mode,
      termId: currentTerm?.id || '',
      additionalCredits: 1,
      reason: '',
    });
  };

  const handleCloseGrantCreditModal = () => {
    setGrantCreditModal({
      open: false,
      mode: 'add',
      termId: '',
      additionalCredits: 1,
      reason: '',
    });
  };

  const handleGrantCredit = () => {
    if (!grantCreditModal.termId || grantCreditModal.reason.trim().length < 3) return;

    grantCredit.mutate(
      {
        studentId,
        termId: grantCreditModal.termId,
        additionalCredits: grantCreditModal.additionalCredits,
        reason: grantCreditModal.reason.trim(),
        // Each action has ONE fixed meaning: the family gets a Book button only when it is SENT.
        offerToFamily: grantCreditModal.mode === 'send',
      },
      {
        onSuccess: () => {
          handleCloseGrantCreditModal();
        },
      }
    );
  };

  const handleApproveRequest = (requestId: string) => {
    approveRequest.mutate({ requestId });
  };

  const handleOpenRejectModal = (requestId: string) => {
    setRejectModal({
      open: true,
      requestId,
      adminNotes: '',
    });
  };

  const handleCloseRejectModal = () => {
    setRejectModal({
      open: false,
      requestId: '',
      adminNotes: '',
    });
  };

  const handleRejectRequest = () => {
    if (!rejectModal.adminNotes || rejectModal.adminNotes.length < 10) return;

    rejectRequest.mutate(
      {
        requestId: rejectModal.requestId,
        adminNotes: rejectModal.adminNotes,
      },
      {
        onSuccess: () => {
          handleCloseRejectModal();
        },
      }
    );
  };

  // Filter unredeemed eligibilities
  const unredeemedEligibilities = eligibilities?.filter((e) => !e.redeemedAt) || [];
  // Newest first, so the recent booking is the one the list keeps when it truncates.
  const redeemedEligibilities = (eligibilities?.filter((e) => e.redeemedAt) || []).sort(
    (a, b) => new Date(b.redeemedAt!).getTime() - new Date(a.redeemedAt!).getTime()
  );

  // A make-up a PARENT booked (it has a slot; an office make-up redeems the offer without one) that
  // has not happened yet. The office email links here, so it sits above the panels, always visible
  // (#422): the booking is the thing the link promises.
  const upcomingParentMakeups = redeemedEligibilities
    .filter((e) => {
      if (!e.MakeupSlot) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return localDateFromKey(e.MakeupSlot.date) >= today;
    })
    .sort((a, b) => a.MakeupSlot!.date.localeCompare(b.MakeupSlot!.date));

  // Filter pending extra requests
  const pendingRequests = extraRequests?.filter((r) => r.status === 'PENDING') || [];
  const otherRequests = extraRequests?.filter((r) => r.status !== 'PENDING') || [];

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
        Bookings & Credits
      </Typography>

      {/* Upcoming make-ups booked by a parent: always visible, the office email links here (#422) */}
      {upcomingParentMakeups.length > 0 && (
        <Alert severity="info" icon={<SlotIcon />} sx={{ mb: 2 }} data-testid="upcoming-parent-makeups">
          <Typography variant="subtitle2" gutterBottom>
            Make-up booked by a parent
          </Typography>
          {upcomingParentMakeups.map((elig) => (
            <Box key={elig.id} sx={{ mb: 0.5 }}>
              <Typography variant="body2">{bookedSlotLine(elig)}</Typography>
              {elig.creditTerm && (
                <Typography variant="caption" color="text.secondary">
                  Credit from {elig.creditTerm.name}
                </Typography>
              )}
            </Box>
          ))}
        </Alert>
      )}

      {/* Make-up Credits Accordion */}
      <Accordion
        expanded={expandedPanel === 'credits'}
        onChange={handlePanelChange('credits')}
        variant="outlined"
        sx={{ mb: 1 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MakeupIcon color="primary" />
            <Typography variant="subtitle2">Make-up Credits</Typography>
            {credits && (
              <Chip
                label={`${credits.creditsRemaining} remaining`}
                size="small"
                color={credits.creditsRemaining > 0 ? 'success' : 'default'}
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {creditsLoading ? (
            <CircularProgress size={20} />
          ) : !currentTerm ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              No active term. Credits are tracked per term.
            </Alert>
          ) : credits ? (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={3}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="primary">
                      {credits.creditsGranted ?? 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="success.main">
                      {credits.creditsRemaining ?? 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Available
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="text.secondary">
                      {credits.creditsUsed ?? 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Used by office
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="warning.main">
                      {credits.creditsRedeemed ?? 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Booked by family
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              <LinearProgress
                variant="determinate"
                value={credits.creditsGranted > 0 ? (credits.creditsUsed / credits.creditsGranted) * 100 : 0}
                sx={{ height: 8, borderRadius: 4, mb: 2 }}
              />

              <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenGrantCreditModal('send')}
                >
                  Send make-up credit
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenGrantCreditModal('add')}
                >
                  Add more make-up credit
                </Button>
              </Stack>
            </Box>
          ) : (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                No credits for current term ({currentTerm.name}).
              </Alert>
              <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenGrantCreditModal('send')}
                >
                  Send make-up credit
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenGrantCreditModal('add')}
                >
                  Add more make-up credit
                </Button>
              </Stack>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Make-up Eligibilities Accordion */}
      <Accordion
        expanded={expandedPanel === 'eligibilities'}
        onChange={handlePanelChange('eligibilities')}
        variant="outlined"
        sx={{ mb: 1 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SlotIcon color="primary" />
            <Typography variant="subtitle2">Make-up Eligibilities</Typography>
            {unredeemedEligibilities.length > 0 && (
              <Chip
                label={`${unredeemedEligibilities.length} unredeemed`}
                size="small"
                color="warning"
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {eligibilitiesLoading ? (
            <CircularProgress size={20} />
          ) : unredeemedEligibilities.length === 0 && redeemedEligibilities.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No make-up eligibilities. Eligibilities are created when a student has an excused
              absence.
            </Typography>
          ) : (
            <Box>
              {unredeemedEligibilities.length > 0 && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Unredeemed ({unredeemedEligibilities.length})
                  </Typography>
                  <List dense sx={{ mb: 2 }}>
                    {unredeemedEligibilities.map((elig) => (
                      <ListItem key={elig.id} sx={{ px: 0 }}>
                        <ListItemText
                          primary={elig.Instrument?.name ?? 'Any instrument'}
                          secondary={
                            <>
                              {elig.adminOffered
                                ? 'Credit sent to the family'
                                : <>
                                    Missed: {elig.Lesson?.name}
                                    {elig.Attendance?.date ? <> on {dayAu(elig.Attendance.date)}</> : null}
                                    <br />
                                    Reason: {elig.Attendance?.absenceReason || 'Not specified'}
                                  </>}
                              {elig.creditTerm && (
                                <>
                                  <br />
                                  {elig.expired
                                    ? `${elig.creditTerm.name}: expired${elig.validUntil ? ` ${dayAu(elig.validUntil)}` : ''}`
                                    : `${elig.creditTerm.name}${elig.validUntil ? `: use by ${dayAu(elig.validUntil)}` : ''}`}
                                </>
                              )}
                            </>
                          }
                        />
                        <Chip
                          label={elig.expired ? 'Expired' : 'Available'}
                          size="small"
                          color={elig.expired ? 'default' : 'warning'}
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}

              {redeemedEligibilities.length > 0 && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Redeemed ({redeemedEligibilities.length})
                  </Typography>
                  <List dense>
                    {redeemedEligibilities.slice(0, 3).map((elig) => (
                      <ListItem key={elig.id} sx={{ px: 0, opacity: 0.7 }}>
                        <ListItemText
                          primary={elig.Instrument?.name ?? elig.MakeupSlot?.Instrument?.name ?? 'Make-up'}
                          secondary={
                            <>
                              {bookedSlotLine(elig) ?? `Redeemed on ${instantDayAu(elig.redeemedAt!, schoolTimezone)}`}
                              {elig.MakeupSlot && (
                                <>
                                  <br />
                                  Booked by a parent on {instantDayAu(elig.redeemedAt!, schoolTimezone)}
                                </>
                              )}
                              {elig.creditTerm && (
                                <>
                                  <br />
                                  Credit from {elig.creditTerm.name}
                                </>
                              )}
                            </>
                          }
                        />
                        <Chip label="Used" size="small" color="success" />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Extra Lesson Requests Accordion */}
      <Accordion
        expanded={expandedPanel === 'extra'}
        onChange={handlePanelChange('extra')}
        variant="outlined"
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ExtraIcon color="primary" />
            <Typography variant="subtitle2">Extra Lesson Requests</Typography>
            {pendingRequests.length > 0 && (
              <Chip
                label={`${pendingRequests.length} pending`}
                size="small"
                color="warning"
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {requestsLoading ? (
            <CircularProgress size={20} />
          ) : extraRequests?.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No extra lesson requests from this student's family.
            </Typography>
          ) : (
            <Box>
              {pendingRequests.length > 0 && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Pending Approval ({pendingRequests.length})
                  </Typography>
                  <Stack spacing={1} sx={{ mb: 2 }}>
                    {pendingRequests.map((request) => (
                      <Paper key={request.id} variant="outlined" sx={{ p: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {request.instrument?.name || 'Any instrument'}
                              {request.lessonType && ` - ${request.lessonType.name}`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Requested {format(new Date(request.createdAt), 'd MMM yyyy')}
                            </Typography>
                          </Box>
                          <Chip
                            label={request.status}
                            size="small"
                            color={getStatusColor(request.status)}
                            icon={getStatusIcon(request.status) || undefined}
                          />
                        </Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          {request.reason}
                        </Typography>
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            onClick={() => handleApproveRequest(request.id)}
                            disabled={approveRequest.isPending}
                          >
                            Approve
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => handleOpenRejectModal(request.id)}
                            disabled={rejectRequest.isPending}
                          >
                            Reject
                          </Button>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                </>
              )}

              {otherRequests.length > 0 && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Other Requests ({otherRequests.length})
                  </Typography>
                  <List dense>
                    {otherRequests.slice(0, 5).map((request) => (
                      <ListItem key={request.id} sx={{ px: 0 }}>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2">
                                {request.instrument?.name || 'Any'}
                              </Typography>
                              <Chip
                                label={request.status}
                                size="small"
                                color={getStatusColor(request.status)}
                              />
                            </Box>
                          }
                          secondary={
                            <>
                              {format(new Date(request.createdAt), 'd MMM yyyy')}
                              {request.makeupForLesson && (
                                <Box component="span" sx={{ display: 'block' }}>
                                  Make-up for {request.makeupForLesson.name} ({request.makeupForLesson.term.name})
                                </Box>
                              )}
                              {request.staffNote && (
                                <Box component="span" sx={{ display: 'block', fontStyle: 'italic' }}>
                                  Staff note: {request.staffNote}
                                </Box>
                              )}
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Send / Add more make-up credit modal (#574): one dialog, two fixed meanings */}
      <Dialog open={grantCreditModal.open} onClose={handleCloseGrantCreditModal}>
        <DialogTitle>
          {grantCreditModal.mode === 'send' ? 'Send make-up credit' : 'Add more make-up credit'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {grantCreditModal.mode === 'send'
              ? `Send make-up credit to ${studentName}'s family. They get a Book button on their make-up page and book it themselves.`
              : `Add more make-up credit for ${studentName}. It is for you to use when you book a make-up from Book Extra Class; the family sees nothing.`}
          </Typography>
          <TextField
            fullWidth
            label="Number of Credits"
            type="number"
            value={grantCreditModal.additionalCredits}
            onChange={(e) =>
              setGrantCreditModal({
                ...grantCreditModal,
                // Clamped to what the server accepts (1 to 10): typing 15 used to leave the button
                // enabled and the server answered 400 with nothing shown (#574 QA).
                additionalCredits: Math.min(10, Math.max(1, parseInt(e.target.value) || 1)),
              })
            }
            inputProps={{ min: 1, max: 10 }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            required
            label="Reason"
            value={grantCreditModal.reason}
            onChange={(e) =>
              setGrantCreditModal({ ...grantCreditModal, reason: e.target.value })
            }
            multiline
            rows={2}
            inputProps={{ maxLength: 500 }}
            error={grantCreditModal.reason.length > 0 && grantCreditModal.reason.trim().length < 3}
            helperText={`Minimum 3 characters (${grantCreditModal.reason.length}/500)`}
            sx={{ mb: 2 }}
          />
          {terms && (
            <TextField
              fullWidth
              select
              label="Term"
              value={grantCreditModal.termId}
              onChange={(e) =>
                setGrantCreditModal({ ...grantCreditModal, termId: e.target.value })
              }
              SelectProps={{ native: true }}
            >
              <option value="">Select term</option>
              {terms.map((term) => (
                <option key={term.id} value={term.id}>
                  {term.name}
                </option>
              ))}
            </TextField>
          )}
          {grantCredit.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {grantCredit.error instanceof Error
                ? grantCredit.error.message
                : 'The credit could not be added. Please try again.'}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseGrantCreditModal}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleGrantCredit}
            disabled={!grantCreditModal.termId || grantCreditModal.reason.trim().length < 3 || grantCredit.isPending}
          >
            {grantCredit.isPending ? (
              <CircularProgress size={20} />
            ) : grantCreditModal.mode === 'send' ? (
              'Send credit'
            ) : (
              'Add credit'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={rejectModal.open} onClose={handleCloseRejectModal}>
        <DialogTitle>Reject Extra Lesson Request</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please provide a reason for rejecting this request.
          </Typography>
          <TextField
            fullWidth
            label="Rejection Reason"
            value={rejectModal.adminNotes}
            onChange={(e) => setRejectModal({ ...rejectModal, adminNotes: e.target.value })}
            multiline
            rows={3}
            error={rejectModal.adminNotes.length > 0 && rejectModal.adminNotes.length < 10}
            helperText="Minimum 10 characters"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRejectModal}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRejectRequest}
            disabled={rejectModal.adminNotes.length < 10 || rejectRequest.isPending}
          >
            {rejectRequest.isPending ? <CircularProgress size={20} /> : 'Reject Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
